import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput as AriaDateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  I18nProvider,
} from 'react-aria-components';
import { inputWrapper } from '../input/variants';
import { twMerge } from 'tailwind-merge';
import { input, InputVariants } from '../../patterns/input';
import { dialog } from '../../patterns/dialog';
import { Typography } from '../../ui';
import { IconArrowLeft, IconArrowRight, IconCalendarAlt } from '@maia/icons';
import { match, P } from 'ts-pattern';
import { ComponentProps, ReactNode } from 'react';

type Props = {
  icon?: ReactNode;
  tooltipSlot?: ReactNode;
  includePicker?: boolean;
  fieldSize?: InputVariants['size'];
} & Omit<ComponentProps<typeof DatePicker>, 'className'>;

export function DateInput(props: Props) {
  const {
    icon,
    tooltipSlot,
    fieldSize = 'default',
    includePicker = false,
    ...rest
  } = props;

  return (
    <I18nProvider locale="nl-NL-u-ca-gregory">
      <DatePicker {...rest}>
        <Group className={twMerge(inputWrapper({ size: fieldSize }))}>
          <AriaDateInput
            className={twMerge(
              input({ size: fieldSize }),
              'placeholder:font-light',
              'col-start-1',
              'col-end-4',
              'row-start-1',
              'row-end-1',
              'peer',
              'gap-x-4',
              icon && mapSizeToIconPadding(fieldSize),
              tooltipSlot && 'pr-48',
            )}
          >
            {(segment) =>
              match(segment)
                .with({ type: 'literal' }, () => (
                  <DateSegment
                    className="literal text-grayscale300 spacer"
                    segment={segment}
                  />
                ))
                .otherwise(() => (
                  <DateSegment
                    className="data-[placeholder='true']:text-grayscale300 outline-info300"
                    segment={segment}
                  />
                ))
            }
          </AriaDateInput>

          {icon && (
            <span
              aria-hidden
              className={twMerge(
                'col-start-1',
                'col-end-1',
                'row-start-1',
                'row-end-1',
                'pointer-events-none',
                'place-self-center',
                'leading-0',
                'transition-colors',
                'duration-200',
                'ease-out',
                'fill-grayscale300',
                'peer-data-[hovered="true"]:fill-primaryMain',
                'peer-data-[focused="true"]:fill-primaryMain',
              )}
            >
              {icon}
            </span>
          )}

          {(includePicker || tooltipSlot) && (
            <div className="flex justify-center items-center col-start-3 col-end-4 row-start-1 row-end-1">
              {match({ includePicker, tooltipSlot })
                .with({ includePicker: true }, () => (
                  <Button className="flex justify-center items-center">
                    <IconCalendarAlt
                      className={twMerge(
                        mapSizeToIconSize(fieldSize),
                        'fill-info300',
                      )}
                    />
                  </Button>
                ))
                .with({ tooltipSlot: P.not(undefined) }, () => tooltipSlot)
                .otherwise(() => null)}
            </div>
          )}
        </Group>

        {includePicker && (
          <Popover>
            <Dialog className={twMerge(dialog(), 'p-24')}>
              <Calendar>
                <header className="flex justify-between items-center mb-24">
                  <Button slot="previous">
                    <IconArrowLeft className="fill-info300 min-w-24 w-24 h-24" />
                  </Button>
                  <Typography
                    variant="body-s"
                    weight="medium"
                    className="text-grayscale500"
                  >
                    <Heading />
                  </Typography>
                  <Button slot="next">
                    <IconArrowRight className="fill-info300 min-w-24 w-24 h-24" />
                  </Button>
                </header>
                <CalendarGrid>
                  <CalendarGridHeader>
                    {(day) => (
                      <CalendarHeaderCell
                        className={twMerge(
                          'text-center',
                          'size-[28px]',
                          'hover:bg-primary50',
                          'font-extralight',
                          'rounded-6',
                          'text-grayscale200',
                          'text-sm',
                          'leading-[28px]',
                        )}
                      >
                        {day}
                      </CalendarHeaderCell>
                    )}
                  </CalendarGridHeader>
                  <CalendarGridBody>
                    {(date) => (
                      <CalendarCell
                        date={date}
                        className={twMerge(
                          'text-center',
                          'size-[28px]',
                          'hover:bg-primary50',
                          'font-extralight',
                          'rounded-6',
                          'text-grayscale600',
                          'data-[disabled="true"]:text-grayscale200',
                          'data-[selected="true"]:bg-primary400',
                          'data-[selected="true"]:text-grayscale0',
                          'text-sm',
                          'leading-[28px]',
                        )}
                      />
                    )}
                  </CalendarGridBody>
                </CalendarGrid>

                {/* <CalendarGrid>
              {(date) => <CalendarCell date={date} />}
            </CalendarGrid> */}
              </Calendar>
            </Dialog>
          </Popover>
        )}
      </DatePicker>
    </I18nProvider>
  );
}

function mapSizeToIconPadding(size: InputVariants['size']): string {
  return match(size)
    .returnType<string>()
    .with('small', () => 'pl-40')
    .with('default', () => 'pl-48')
    .with('large', () => 'pl-56')
    .otherwise(() => 'pl-56');
}

function mapSizeToIconSize(size: InputVariants['size']): string {
  return match(size)
    .returnType<string>()
    .with('small', () => 'size-20')
    .with('default', () => 'size-20')
    .with('large', () => 'size-24')
    .otherwise(() => 'size-24');
}
